import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors, SIZES, Colors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { AVISOS_SIN_NAVEGACION, destinoDeAvisoDeComida, esAvisoDeComida } from '../services/rutaDeAviso';

// FIX BUG #5 — Ícono según el tipo de notificación (data.type que guarda el backend).
function iconForType(type: any): string {
  const t = String(type ?? '').toUpperCase();
  if (t.includes('RIDE')) return 'car';
  if (t.includes('ORDER')) return 'fast-food';
  if (t.includes('PENALTY')) return 'alert-circle';
  if (t.includes('RECHARGE')) return 'wallet';
  if (t.includes('DEVIATION')) return 'git-branch';
  if (t.includes('CRM') || t.includes('CANDIDATE')) return 'people';
  return 'notifications';
}

export default function NotificationsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [notifications, setNotifications] = useState<any[]>([]);

  // H72 (auditoría 16-sep-2026): el estado inicial es `[]`, así que mientras
  // llegaba la primera respuesta la pantalla afirmaba "No tienes
  // notificaciones nuevas" aunque hubiera diez sin leer — con red lenta, un
  // buen rato. Mismo patrón que (tabs)/activity.tsx: se espera al PRIMER aviso
  // del listener antes de decir que no hay nada. Derivado del uid: sin sesión
  // no hay nada que esperar y no puede quedarse en "Cargando…".
  // (El listener llama con `[]` también cuando falla, así que siempre llega.)
  const [notificacionesRecibidas, setNotificacionesRecibidas] = useState(false);
  const cargandoNotificaciones = !!uid && !notificacionesRecibidas;

  // FIX BUG #5 — Antes la lista era `[]` hardcodeado y NUNCA leía nada. Ahora
  // escucha en vivo /users/{uid}/notifications (lo puebla el backend en
  // sendPushToUid). Al abrir la pantalla marcamos todo como leído.
  useEffect(() => {
    if (!uid) return;
    setNotificacionesRecibidas(false);
    const unsub = firestoreService.listenToUserNotifications(uid, (items) => {
      setNotificacionesRecibidas(true);
      const mapped = items.map((n: any) => ({
        id: n.id,
        unread: n.read !== true,
        icon: iconForType(n.type ?? n.data?.type),
        type: n.type ?? n.data?.type ?? '',
        orderId: n.orderId ?? n.data?.orderId ?? null,
        title: String(n.title ?? 'Notificación'),
        message: String(n.body ?? ''),
        time: n.createdAt?.toDate?.()?.toLocaleString('es-DO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) ?? '',
      }));
      setNotifications(mapped);
      // Marcar como leídas las no-leídas (limpia el contador de la campana).
      const unreadIds = items.filter((n: any) => n.read !== true).map((n: any) => n.id);
      if (unreadIds.length > 0) firestoreService.markAllNotificationsRead(uid, unreadIds).catch(() => {});
    });
    return () => { try { (unsub as any)?.(); } catch {} };
  }, [uid]);

  const handleDelete = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id)); // optimista
    if (uid) firestoreService.deleteNotification(uid, id).catch(() => {});
  };

  // FIX T2-9 — Tap sobre la tarjeta abre el destino apropiado según el tipo.
  // El listener del useEffect ya marca todo como leído al montar, así que aquí
  // solo enrutamos.
  const handleOpenNotif = async (notif: any) => {
    // OJO: usar `||`, no `??`. El mapeo del listener pone type:'' (string vacío)
    // cuando no hay tipo, y `??` solo cae con null/undefined — con '' se quedaría
    // con el string vacío y el fallback por ícono nunca correría.
    const type = String(notif?.type || notif?.icon || '').toUpperCase();
    // El SOS y los casos de fraude los atiende el admin desde el panel web.
    if (AVISOS_SIN_NAVEGACION.has(type)) return;
    // V37 — la campana es una pantalla del stack raíz ENCIMA de (tabs):
    // `push` apilaba otra copia de las pestañas y un segundo mapa con sus
    // listeners. `dismissTo` vuelve al mapa de abajo y cierra la campana.
    if (type.includes('RIDE') || type.includes('CAR')) { router.dismissTo('/(tabs)/map'); return; }
    if (type === 'CREDITO_EN_REVISION' || type === 'DRIVER_TRIAL_EXPIRED' || type === 'PAYMENT_BLOCK_CLEARED' || type === 'MEMBERSHIP_PAYMENT_DUE' || type === 'MONTHLY_CYCLE' || type === 'CANCELLATION_COMPENSATION' || type === 'CUOTA_PAGADA') { router.push('/wallet'); return; }
    // La multa se ve y se paga en el PenaltyGate del mapa, no en la billetera.
    if (type === 'PENALTY_BLOCKED' || type === 'PENALTY_WARNING') { router.dismissTo('/(tabs)/map'); return; }
    if (type.includes('ORDER') || type.includes('DELIVERY') || type.includes('FAST-FOOD')) {
      // V18 — cada lado del pedido a SU pantalla: /order-tracking es la del
      // cliente; el restaurante y el repartidor veían ahí "Tu PIN al repartidor".
      const s = useAppStore.getState();
      const destino = await destinoDeAvisoDeComida(
        esAvisoDeComida(type) ? type : 'ORDER_ASSIGNED',
        notif?.orderId ? String(notif.orderId) : null,
        s.userFirebaseUid ?? null,
        s.userRole ?? null
      );
      if (!destino) return;
      if (destino.pathname.startsWith('/(tabs)/')) router.dismissTo(destino.pathname as any);
      else router.push({ pathname: destino.pathname, params: destino.params ?? {} } as any);
      return;
    }
    if (type.includes('PENALTY') || type.includes('WALLET') || type.includes('RECHARGE') || type.includes('ALERT-CIRCLE')) { router.push('/wallet'); return; }
    // ⚠️ ESTOS TRES AVISOS SON PARA EL CANDIDATO, NO PARA EL DUEÑO, y
    // caían en la regla de abajo que manda al CRM — una pantalla que el
    // candidato no puede ni abrir (las reglas de `crm_drivers` son del dueño).
    // Tocaba «Te aprobaron el vehículo» y aterrizaba en un sitio vacío.
    if (type === 'CRM_CANDIDATE_APPROVED' || type === 'CRM_CANDIDATE_REJECTED' || type === 'CRM_VEHICLE_RELEASED') {
      router.push('/mi-postulacion' as any);
      return;
    }
    if (type.includes('CRM') || type.includes('CANDIDATE') || type.includes('PEOPLE')) { router.push('/crm-settings'); return; }
    // Fallback: sin ruta clara, no hacer nada.
  };

  const handleClearAll = () => {
    const ids = notifications.map((n) => n.id);
    setNotifications([]); // optimista
    if (uid) ids.forEach((id) => firestoreService.deleteNotification(uid, id).catch(() => {}));
  };

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { borderBottomColor: colors.border, backgroundColor: colors.card }]}>
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Notificaciones</Text>
        {notifications.length > 0 ? (
          <TouchableOpacity onPress={handleClearAll} style={styles.clearButton}>
            <Text style={{color: colors.primary, fontWeight: 'bold'}}>Limpiar</Text>
          </TouchableOpacity>
        ) : (
          <View style={{ width: 60 }} />
        )}
      </View>

      <ScrollView contentContainerStyle={styles.container}>
        {notifications.length === 0 && cargandoNotificaciones ? (
          <Text style={{textAlign: 'center', marginTop: 40, color: colors.textSecondary}}>Cargando tus notificaciones…</Text>
        ) : notifications.length === 0 ? (
          <Text style={{textAlign: 'center', marginTop: 40, color: colors.textSecondary}}>No tienes notificaciones nuevas.</Text>
        ) : (
          notifications.map((notif) => (
            <TouchableOpacity key={notif.id} activeOpacity={0.8} onPress={() => handleOpenNotif(notif)} style={[styles.notificationCard, { backgroundColor: notif.unread ? 'rgba(0, 200, 83, 0.05)' : colors.card }]}>
               <View style={[styles.iconContainer, { backgroundColor: notif.unread ? colors.accent : colors.border }]}>
                  <Ionicons name={notif.icon as any} size={20} color={notif.unread ? colors.white : colors.textSecondary} />
               </View>
               <View style={styles.textContainer}>
                  <Text style={[styles.notifTitle, { color: colors.text }]}>{notif.title}</Text>
                  <Text style={[styles.notifMessage, { color: colors.textSecondary }]}>{notif.message}</Text>
                  <Text style={[styles.notifTime, { color: colors.textSecondary }]}>{notif.time}</Text>
               </View>

               {notif.unread && <View style={[styles.unreadDot, { backgroundColor: colors.accent }]} />}

               <Pressable
                 onPress={(e: any) => { try { e?.stopPropagation?.(); } catch {} handleDelete(notif.id); }}
                 hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                 style={({ hovered }: any) => [
                   styles.deleteButton,
                   { backgroundColor: hovered ? 'rgba(255,59,48,0.1)' : 'transparent' }
                 ]}
               >
                 <Ionicons name="trash-outline" size={20} color={Colors.error} />
               </Pressable>
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SIZES.padding,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  backButton: { padding: 4 },
  headerTitle: { fontSize: 18, fontWeight: 'bold' },
  clearButton: { padding: 8, minWidth: 60, alignItems: 'flex-end' },
  container: { padding: SIZES.padding },
  notificationCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: SIZES.radius,
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 5,
    elevation: 2,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  textContainer: { flex: 1, marginRight: 8 },
  notifTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 4 },
  notifMessage: { fontSize: 14, lineHeight: 20, marginBottom: 8 },
  notifTime: { fontSize: 12, fontStyle: 'italic' },
  unreadDot: { width: 10, height: 10, borderRadius: 5, marginRight: 12 },
  deleteButton: {
    padding: 10,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  }
});

